/* eslint-disable @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any,@typescript-eslint/no-shadow */
import log from 'loglevel';
import moment from 'moment';
import i18n from 'i18next';
import { LOCAL_STORAGE_KEYS, AUTH_CONFIG } from '../consts';
import STRING_KEYS from '../language/keys';
import { ISRAEL_COUNTRY_CODE, UNITED_KINGDOM_COUNTRY_CODE, UNITED_STATES_COUNTRY_CODE } from '../components/LocationAutocomplete';
import { ExactLocation, LocationRegion, PositionLocations } from '../@types/position';

export type Tokens = {
  tokenType: string,
  accessToken: string,
  refreshToken: string,
  expired: string,
}

const TAG = '[APP_UTILS]';
/**
 * check if email address is valid
 * more info: https://stackoverflow.com/a/46181
 * @param email
 * @returns bool
 */
export const validateEmail = (email: string): boolean => {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(String(email).toLowerCase());
};

/**
 * Remove items from an array
 * @param arr input Array
 * @param itemToRemove
 * @returns Array without the items that were removed
 */
export const removeFromArray = (arr: Array<string>, itemToRemove: string): Array<string> => {
  return arr.filter((item) => {
    return item !== itemToRemove;
  });
};

/**
 * Async delay function
 * @param ms how many milliseconds to be delay
 * @returns Promise
 */
export const delay =
  (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get access token from local storage
 * @returns String || null
 */
export const getUserTokens = (): Tokens | null => {
  const strTokens = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_TOKENS) || '';
  try {
    const tokens = JSON.parse(strTokens);
    if  (!tokens) {
      return null;
    }

    return tokens;
  } catch (e) {
    log.warn(TAG, 'cannot parse tokens', strTokens);

    return null;
  }
};

/**
 * Set user tokens to local storage
 * @returns void
 */
export const setUserTokens = (tokens: Tokens): void => {
  const {
    tokenType, accessToken, refreshToken, expired,
  } = tokens;
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER_TOKENS, JSON.stringify({
    tokenType,
    accessToken,
    refreshToken,
    expired,
  }));
};

/**
 * Remove tokens from local storage
 * @returns void
 */
export const deleteUserTokens = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_TOKENS);
};

/**
 * Check access token expired
 */
export const isTokenExpired = (): boolean => {
  const { expired } = getUserTokens() || {};
  const expirationDate = moment(expired);
  if (!expired) {
    return true;
  }

  return moment().isAfter(expirationDate);
};

/**
 * Calculate token expiration date
 * @param expiresSec expires time in seconds
 */
export const tokenExpirationDate = (expiresSec: number): string => {
  return moment().add(expiresSec - AUTH_CONFIG.EXPIRES_DELTA, 'seconds').format();
};

/**
 * Convert flirts character to Upper case else all characters to lower case
 * @param word string
 * @return string
 */
export const firstCharToUpper = (
  word: string,
): string => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Generate statuses for table filters
 * @param statuses
 * @return Object {label: string, value: string}
 */
export const generateStatuses = (
  statuses: { [key: string]: string },
): { label: string, value: string }[] => Object.values(statuses)
  .map((status: string) => ({
    label: status.split('_').reduce((acc: string, word: string) => {
      const convertedWord = firstCharToUpper(word);

      return `${acc} ${convertedWord}`;
    }, ''),
    value: status.toUpperCase(),
  }));
// Compare two objects of the same type and return the differing fields from the second object
export const compareObjects = <T>(obj1: T, obj2: T): Partial<T> => {
  const differingFields: Partial<T> = {};
  Object.keys(obj1).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(obj1, key) && Object.prototype.hasOwnProperty.call(obj2, key)) {
      if (obj1[key as keyof T] !== obj2[key as keyof T]) {
        differingFields[key as keyof T] = obj2[key as keyof T];
      }
    }
  });

  return differingFields;
};

export const createActiveFilterObject = (
  fieldName: string,
  label: string,
  value: string,
  displayValue?: string,
) => {
  return {
    label,
    field: fieldName,
    value,
    displayValue: displayValue || value,
  };
};

export const getFirstLetters = (string: string): string => {
  return string
    .split(' ')
    .map((word: string) => word[0])
    .join('');
};

/**
 * set subdomain to lower-case and remove not all-numeric chars
 * for example:
 *  input value - "It's Perfect 1st Company!"
 *  output value = itsperfect1stcompany"
 * @param name
 * @returns engagementAction
 */
export const generateSubdomain = (name: string): string => {
  return name.replace(/[^A-z0-9]/g, '').toLowerCase();
};

export const isLinkedinUrl = (searchValue: string) => {
  // eslint-disable-next-line max-len
  const linkedInRegex = /((https?:\/\/)?((www|\w\w)\.)?linkedin\.com\/)((([\w]{2,3})?)|([^]+\/(([\w|\d-&#?=])+\/?){1,}))$/m;

  return searchValue.match(linkedInRegex);
};

export const getServerError = (error: ServerError) => {
  if (error.response?.data?.fieldsErrors?.length > 0) {
    return error?.response?.data.fieldsErrors[0];
  }

  if (error.response?.data?.message) {
    return error.response?.data?.message;
  }

  return error.message || 'failed to load data from server';
};

/**
 * check if value match url .
 * @param value string
 * @returns indicator (boolean) if value match url
 */
export const isLinkedInCompanyFormat = (value: string): boolean => {
  return !!value?.match('linkedin.com/company/*');
};

export const formatExactLocation = (location: ExactLocation): string => {
  const stateString = location?.city ? location?.state : location?.stateLong;

  return [
    location?.city,
    location?.country !== ISRAEL_COUNTRY_CODE ? stateString : undefined,
    location?.countryLong || location?.country,
  ].filter(Boolean).join(', ');
};

export const formatRegion = (location: LocationRegion): string => {
  if (location.country === location.region) {
    let translationKey = STRING_KEYS.SPECIFIC_COUNTRY_REMOTE;
    if (location.country === UNITED_STATES_COUNTRY_CODE || location.country === UNITED_KINGDOM_COUNTRY_CODE) {
      translationKey = STRING_KEYS.US_UK_COUNTRY_REMOTE;
    }

    return i18n.t(translationKey, { countryCode: location.countryLong || location.country });
  }

  return `${location?.region}, ${location?.countryLong || location?.country}`;
};

export const formatLocations = (locations: PositionLocations): string[] => {
  return [
    ...(locations?.exactLocations || []).map(formatExactLocation),
    ...(locations?.regions || []).map(formatRegion),
  ];
};

export const getEnumValuesInRange = <T extends string>(enumObj: Record<string, T>, arr: T[]): T[] => {
  if (arr.length <= 1) {
    return arr;
  }

  const enumKeys = Object.values(enumObj) as T[];
  const sortedRange = arr.slice().sort((a, b) => enumKeys.indexOf(a) - enumKeys.indexOf(b));

  const startIndex = enumKeys.indexOf(sortedRange[0]);
  const endIndex = enumKeys.indexOf(sortedRange[sortedRange.length - 1]);

  if (startIndex === -1 || endIndex === -1) {
    return [];
  }

  return enumKeys.slice(startIndex, endIndex + 1);
};

export const getMinimalEnumValuesSequence = <T extends string>(enumObj: Record<string, T>, arr: T[]): T[] => {
  if (arr.length <= 1) {
    return arr;
  }

  const enumKeys = Object.values(enumObj) as T[];
  const sortedRange = arr.slice().sort((a, b) => enumKeys.indexOf(a) - enumKeys.indexOf(b));
  const rangeIndexes = sortedRange.map((enumValue) => enumKeys.indexOf(enumValue));

  let endIndex = 0;
  for (let i = 0; i < rangeIndexes.length; i++) {
    if (rangeIndexes[i + 1] !== undefined) {
      const enumIndex = rangeIndexes[i];
      if (enumIndex + 1 === rangeIndexes[i + 1]) {
        endIndex = i + 1;
      } else {
        break;
      }
    }
  }

  return sortedRange.slice(0, endIndex + 1);
};
